import React, { useState, useCallback } from 'react';
import { ReactSortable } from 'react-sortablejs';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import LapSponsorshipCard from './LapSponsorshipCard';
import {
  Container,
  Header,
  HeaderTitle,
  HelperBullets,
  ListContainer,
  SortableContainer,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  LoadingContainer,
  ButtonWrapper,
} from './StyledComponents';

// Icon for empty state
const TrophyIcon = () => (
  <svg
    width="40"
    height="40"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
    <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
    <path d="M4 22h16" />
    <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
    <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
    <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
  </svg>
);

// Generate unique ID for new sponsorships
let sponsorshipIdCounter = 0;
const generateSponsorshipId = () => {
  sponsorshipIdCounter += 1;
  return `lap-sponsorship-${Date.now()}-${sponsorshipIdCounter}`;
};

// Get highest order number
const getHighestOrder = (sponsorships) => {
  if (!sponsorships || sponsorships.length === 0) return 0;
  return Math.max(...sponsorships.map((s) => s.order || 0));
};

// State is lifted to parent (EditEventV2) so it persists across tab switches
const LapSponsorshipTab = ({
  event: _event,
  loading,
  sponsorships = [],
  onSponsorshipsChange,
}) => {
  // Only expandedIds is local UI state - it can reset on tab switch
  const [expandedIds, setExpandedIds] = useState(new Set());

  // Add new sponsorship
  const handleAddSponsorship = useCallback(() => {
    const newSponsorship = {
      id: generateSponsorshipId(),
      name: '',
      num_laps: null,
      price: null,
      order: getHighestOrder(sponsorships) + 1,
      payout_1: null,
      payout_1_label: null,
      payout_2: null,
      payout_2_label: null,
      payout_3: null,
      payout_3_label: null,
    };
    onSponsorshipsChange([...sponsorships, newSponsorship]);
    // Collapse all others and only expand the new card
    setExpandedIds(new Set([newSponsorship.id]));
  }, [sponsorships, onSponsorshipsChange]);

  // Update sponsorship
  const handleUpdateSponsorship = useCallback(
    (updatedSponsorship) => {
      const updated = sponsorships.map((s) =>
        s.id === updatedSponsorship.id ? updatedSponsorship : s
      );
      onSponsorshipsChange(updated);
    },
    [sponsorships, onSponsorshipsChange]
  );

  // Delete sponsorship
  const handleDeleteSponsorship = useCallback(
    (id) => {
      onSponsorshipsChange(sponsorships.filter((s) => s.id !== id));
      setExpandedIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    },
    [sponsorships, onSponsorshipsChange]
  );

  // Toggle expand/collapse
  const handleToggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }, []);

  // Handle reorder via drag and drop
  const handleSetList = useCallback(
    (newList) => {
      // Check if order actually changed
      const oldIds = sponsorships.map((s) => s.id).join(',');
      const newIds = newList.map((s) => s.id).join(',');

      if (oldIds === newIds) return;

      // Update order values based on new positions
      const reordered = newList.map((s, idx) => ({
        ...s,
        order: idx + 1,
      }));
      onSponsorshipsChange(reordered);
    },
    [sponsorships, onSponsorshipsChange]
  );

  if (loading) {
    return (
      <Card>
        <LoadingContainer>Loading lap sponsorships...</LoadingContainer>
      </Card>
    );
  }

  return (
    <Container>
      <Card>
        <Header>
          <HeaderTitle>Lap Sponsorships</HeaderTitle>
          <ButtonWrapper>
            <ButtonV2 type="button" elevated onClick={handleAddSponsorship}>
              + Add Race
            </ButtonV2>
          </ButtonWrapper>
        </Header>

        <HelperBullets>
          <li>Let fans sponsor individual laps</li>
          <li>Set price per lap</li>
          <li>Define payout distribution</li>
        </HelperBullets>

        {sponsorships.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <TrophyIcon />
            </EmptyIcon>
            <EmptyTitle>No races yet</EmptyTitle>
            <EmptyDescription>
              Configure races with lap counts, pricing, and payouts
            </EmptyDescription>
            <ButtonWrapper>
              <ButtonV2 type="button" onClick={handleAddSponsorship}>
                + Add Race
              </ButtonV2>
            </ButtonWrapper>
          </EmptyState>
        ) : (
          <ListContainer>
            <ReactSortable
              tag={SortableContainer}
              list={sponsorships}
              setList={handleSetList}
              animation={200}
              handle=".drag-handle"
              ghostClass="sortable-ghost"
              chosenClass="sortable-chosen"
            >
              {sponsorships.map((sponsorship) => (
                <div key={sponsorship.id} data-id={sponsorship.id}>
                  <LapSponsorshipCard
                    sponsorship={sponsorship}
                    expanded={expandedIds.has(sponsorship.id)}
                    onToggleExpand={() => handleToggleExpand(sponsorship.id)}
                    onUpdate={handleUpdateSponsorship}
                    onDelete={() => handleDeleteSponsorship(sponsorship.id)}
                  />
                </div>
              ))}
            </ReactSortable>
          </ListContainer>
        )}
      </Card>
    </Container>
  );
};

export default LapSponsorshipTab;
